import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Search,
  Zap,
  SlidersHorizontal,
  ChevronDown,
  ChevronUp,
  X,
  Check,
  Lock,
  Award,
  Activity,
  ShieldCheck,
  Cpu,
  Users,
  Compass,
  Bookmark,
  Save,
  Trash2,
  Inbox,
  Loader2,
  Landmark,
} from 'lucide-react';
import { Option } from '../../Admin/Types/AdminType';
import { getAllArchitectureExperience, getAllCities, getAllCoreExpertise, getAllDomainExpertise, getAllHiringAuthority, getAllOwnerShipLevel, getAllProductScale, getAllRoleLevel, getAllTargetRole, getAllTechnologyStack, getAllWorkFlexibility, getAllWorkStyle, getAllDecisionSpeed, getAllStartupRiskPreference } from '../../Admin/Services/AdminService';
import { mapToOptions } from '../../Shared/Component/forms/dropdownHelpers';

import { searchCandidates } from '../../Candidate/Services/CandidateService';
import CandidateCardForRecruiter from '../Components/CandidateCardForRecruiter';
import LoadMoreButton from '../../Shared/Pagination/LoadMoreButton';
import { SavedFilterResponse, SaveFilterRequest, SearchFilterResponse } from '../../SearchFilter/Types/SerachFileter';
import { getAllSearchFilters, getAllSearchFiltersBySubscriptionPlan, saveFilter, updateFilter } from '../../SearchFilter/Services/SearchFilterService';
import { storage } from '../../Shared/Utils/authStorage';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';

interface FilterState {
  searchQuery: string;

  // IGNITE
  firstName: string[];
  lastName: string[];
  currentRole: string[];
  currentCompany: string[];
  totalExperience: string[];
  leadershipLevel: string[];
  availabilityStatus: string[];
  location: string[];
  workFlexibility: string[];

  profileVerified: string[];

  // ACCELERATE
  targetRole: string[];
  ownershipLevel: string[];
  teamSizeLed: string[];
  hiringAuthority: string[];
  productScale: string[];
  architectureExperience: string[];
  domainExpertise: string[];
  coreExpertise: string[];
  technologyStack: string[];
  ownerShipLevel: string[];
  workStyle: string[];
  careerIntent: string[];
  decisionSpeed: string[];
  interviewReadiness: string[];
  startupRiskPreference: string[];

  // DOMINATE
  businessImpactType: string[];
  businessImpactDescription: string[];
  complexProblemSolved: string[];
  topAchievements: string[];
  reputationSignals: string[];
  notableCompanies: string[];

  leadershipScore: number;
  architectureScore: number;
  businessImpactScore: number;
  overallScore: number;

  linkedinVerified: string[];
  workEmailVerified: string[];
  phoneVerified: string[];

  expectedCompensation: string[];
  noticePeriod: string[];

  // LEADERSHIP / EXECUTIVE
  yearsInLeadership: string[];
  reportsToLevel: string[];
  highestEducation: string[];
  functionScope: string[];
  geographicScope: string[];
  companyStageExperience: string[];
  transformationExperience: string[];
  executiveEducation: string[];
  languages: string[];
  budgetManagedLakhs: string[];
  revenueScopeCrores: string[];
  travelWillingnessPercent: string[];
  boardOrInvestorExposure: string[];
}

const initialFilters: FilterState = {
  searchQuery: '',
  firstName: [],
  lastName: [],
  currentRole: [],
  currentCompany: [],
  totalExperience: [],
  leadershipLevel: [],
  availabilityStatus: [],
  location: [],
  workFlexibility: [],
  profileVerified: [],
  targetRole: [],
  ownershipLevel: [],
  teamSizeLed: [],
  hiringAuthority: [],
  productScale: [],
  architectureExperience: [],
  domainExpertise: [],
  coreExpertise: [],
  technologyStack: [],
  ownerShipLevel: [],
  workStyle: [],
  careerIntent: [],
  decisionSpeed: [],
  interviewReadiness: [],
  startupRiskPreference: [],
  businessImpactType: [],
  businessImpactDescription: [],
  complexProblemSolved: [],
  topAchievements: [],
  reputationSignals: [],
  notableCompanies: [],
  leadershipScore: 0,
  architectureScore: 0,
  businessImpactScore: 0,
  overallScore: 0,
  linkedinVerified: [],
  workEmailVerified: [],
  phoneVerified: [],
  expectedCompensation: [],
  noticePeriod: [],

  yearsInLeadership: [],
  reportsToLevel: [],
  highestEducation: [],
  functionScope: [],
  geographicScope: [],
  companyStageExperience: [],
  transformationExperience: [],
  executiveEducation: [],
  languages: [],
  budgetManagedLakhs: [],
  revenueScopeCrores: [],
  travelWillingnessPercent: [],
  boardOrInvestorExposure: [],
};

export interface ExecutiveSearchPortalRef {
  applySavedFilter: (
    filterJson: string,
    filter: SavedFilterResponse
  ) => void;
}

const SCORE_LABELS: Record<string, string> = {
  overalScore: "Overall",
  overallScore: "Overall",
  leadershipScore: "Leadership",
  architectureScore: "Architecture",
  businessImpactScore: "Impact",
};

const INTERVIEW_READINESS_OPTIONS = [
  "READY IMMEDIATELY",
  "READY WITHIN FEW DAYS",
  "NEED 1 WEEK PREPARATION",
  "NEED 2 WEEKS PREPARATION",
  "NEED 1 MONTH PREPARATION",
  "EXPLORING ONLY",
];

const CAREER_INTENT_OPTIONS = [
  "ARCHITECT LARGE SCALE SYSTEMS",
  "BUILD SCALABLE PLATFORMS",
  "SOLVE DISTRIBUTED SYSTEM PROBLEMS",
  "DRIVE TECHNICAL EXCELLENCE",
  "LEAD ENGINEERING TEAMS",
  "BUILD AND SCALE TEAMS",
  "MOVE INTO ENGINEERING MANAGEMENT",
  "DRIVE ENGINEERING STRATEGY",
  "BUILD PRODUCT FROM SCRATCH",
  "OWN END TO END PRODUCT",
  "WORK ON 0 TO 1 PRODUCTS",
  "DESIGN SYSTEM ARCHITECTURE",
  "MODERNIZE LEGACY SYSTEMS",
  "DRIVE BUSINESS IMPACT THROUGH TECH",
  "BUILD STARTUP",
];

const ExecutiveSearchPortalLight = forwardRef<ExecutiveSearchPortalRef, {}>(
  (props, ref) => {
    const [filters, setFilters] = useState<FilterState>(initialFilters);

    useImperativeHandle(ref, () => ({
      applySavedFilter(filterJson: string, filter: SavedFilterResponse) {
        try {
          const parsed = JSON.parse(filterJson);

          setSelectedFilter(filter);
          setPage(0);
          setResults([]);
          setDebouncedQuery(parsed.searchQuery ?? "");

          setFilters({
            ...initialFilters,
            ...parsed,
          });
        } catch (error) {
          console.error("Invalid saved filter", error);
        }
      },
    }));

    const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [results, setResults] = useState<any[]>([]);
    const [page, setPage] = useState(0);
    const [pagination, setPagination] = useState({
      page: 0,
      size: 2,
      totalElements: 0,
      totalPages: 0,
      first: true,
      last: false,
      hasNext: false,
      hasPrevious: false,
    });

    /* ------------------------------------------------------------
     * Debounced free-text search: chips/sliders trigger instantly,
     * the text query waits 350ms so we don't hammer the API.
     * ---------------------------------------------------------- */
    const [debouncedQuery, setDebouncedQuery] = useState(filters.searchQuery);
    const debounceTimeout = useRef<any>(null);

    useEffect(() => {
      debounceTimeout.current = setTimeout(() => {
        if (filters.searchQuery !== debouncedQuery) {
          setDebouncedQuery(filters.searchQuery);
        }
      }, 350);
      return () => clearTimeout(debounceTimeout.current);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.searchQuery]);

    const executeSearch = () => {
      clearTimeout(debounceTimeout.current);
      setDebouncedQuery(filters.searchQuery);
    };

    // useMemo keeps a stable reference so the effect below only fires
    // when filters or the debounced query genuinely change (not on re-renders).
    const effectiveFilters: FilterState = useMemo(
      () => ({ ...filters, searchQuery: debouncedQuery }),
      [filters, debouncedQuery]
    );

    useEffect(() => {
      const fetchCandidates = async () => {
        const payload = buildSearchPayload(effectiveFilters);
        setLoading(true);
        try {
          const response = await searchCandidates(payload, page);
          if (page === 0) {
            setResults(response.data.content || []);
          } else {
            setResults((prev) => [...prev, ...(response.data.content || [])]);
          }
          setPagination({
            page: response.data.page,
            size: response.data.size,
            totalElements: response.data.totalElements,
            totalPages: response.data.totalPages,
            first: response.data.first,
            last: response.data.last,
            hasNext: response.data.hasNext,
            hasPrevious: response.data.hasPrevious,
          });
        } finally {
          setLoading(false);
        }
      };
      fetchCandidates();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveFilters, page]);

    const handleCheckboxChange = (category: keyof FilterState, value: string) => {
      setPage(0);
      setResults([]);
      setFilters((prev) => {
        const currentValues = prev[category] as string[];
        const updatedValues = currentValues.includes(value)
          ? currentValues.filter((v) => v !== value)
          : [...currentValues, value];
        return { ...prev, [category]: updatedValues };
      });
    };

    const handleSliderChange = (category: keyof FilterState, value: number) => {
      setPage(0);
      setResults([]);
      setFilters((prev) => ({ ...prev, [category]: value }));
    };

    const clearAllFilters = () => {
      setFilters(initialFilters);
      setDebouncedQuery("");
      setPage(0);
      setResults([]);
      setSelectedFilter(null);
    };

    const [roleOptions, setRoleOptions] = useState<Option<string>[]>([]);
    useEffect(() => {
      getAllTargetRole().then((response) => {
        if (response?.data) {
          setRoleOptions(mapToOptions(response.data, "targetRole", "targetRole"));
        }
      });
    }, []);

    const [currentRoleOptions, setCurrentRoleOptions] = useState<Option<string>[]>([]);
    useEffect(() => {
      getAllTargetRole().then((response) => {
        if (response?.data) {
          setCurrentRoleOptions(mapToOptions(response.data, "targetRole", "targetRole"));
        }
      });
    }, []);

    const [city, setCity] = useState<Option<string>[]>([]);
    useEffect(() => {
      getAllCities().then((response) => {
        if (response?.data) {
          setCity(mapToOptions(response.data, "city", "city"));
        }
      });
    }, []);

    const [roleLevel, setRoleLevel] = useState<Option<string>[]>([]);
    useEffect(() => {
      getAllRoleLevel().then((response) => {
        if (response?.data) {
          setRoleLevel(mapToOptions(response.data, "roleLevel", "roleLevel"));
        }
      });
    }, []);

    const [domain, setDomain] = useState<Option<string>[]>([]);
    useEffect(() => {
      getAllDomainExpertise().then((response) => {
        if (response?.data) {
          setDomain(mapToOptions(response.data, "domainExpertise", "domainExpertise"));
        }
      });
    }, []);

    const [architectureExperience, setArchitectureExperience] = useState<Option<string>[]>([]);
    useEffect(() => {
      getAllArchitectureExperience().then((response) => {
        if (response?.data) {
          setArchitectureExperience(
            mapToOptions(response.data, "architectureExperience", "architectureExperience")
          );
        }
      });
    }, []);

    const [technologyStack, setTechnologyStack] = useState<Option<string>[]>([]);
    useEffect(() => {
      getAllTechnologyStack().then((response) => {
        if (response?.data) {
          setTechnologyStack(
            mapToOptions(response.data, "technologyStack", "technologyStack")
          );
        }
      });
    }, []);

    const [coreExpertise, setCoreExpertise] = useState<Option<string>[]>([]);
    useEffect(() => {
      getAllCoreExpertise().then((response) => {
        if (response?.data) {
          setCoreExpertise(
            mapToOptions(response.data, "coreExpertise", "coreExpertise")
          );
        }
      });
    }, []);

    const [ownerShipLevel, setOwnerShipLevel] = useState<Option<string>[]>([]);
    useEffect(() => {
      getAllOwnerShipLevel().then((response) => {
        if (response?.data) {
          setOwnerShipLevel(
            mapToOptions(response.data, "ownerShipLevel", "ownerShipLevel")
          );
        }
      });
    }, []);

    const [hiringAuthority, setHiringAuthority] = useState<Option<string>[]>([]);
    useEffect(() => {
      getAllHiringAuthority().then((response) => {
        if (response?.data) {
          setHiringAuthority(
            mapToOptions(response.data, "hiringAuthority", "hiringAuthority")
          );
        }
      });
    }, []);

    const [productScale, setProductScale] = useState<Option<string>[]>([]);
    useEffect(() => {
      getAllProductScale().then((response) => {
        if (response?.data) {
          setProductScale(
            mapToOptions(response.data, "productScale", "productScale")
          );
        }
      });
    }, []);

    const [workFlexibility, setWorkFlexibility] = useState<Option<string>[]>([]);
    useEffect(() => {
      getAllWorkFlexibility().then((response) => {
        if (response?.data) {
          setWorkFlexibility(
            mapToOptions(response.data, "workFlexibility", "workFlexibility")
          );
        }
      });
    }, []);

    const [workStyleOptions, setWorkStyleOptions] = useState<Option<string>[]>([]);
    useEffect(() => {
      getAllWorkStyle().then((response) => {
        if (response?.data) {
          setWorkStyleOptions(mapToOptions(response.data, "workStyle", "workStyle"));
        }
      });
    }, []);

    const [decisionSpeedOptions, setDecisionSpeedOptions] = useState<Option<string>[]>([]);
    useEffect(() => {
      getAllDecisionSpeed().then((response) => {
        if (response?.data) {
          setDecisionSpeedOptions(
            mapToOptions(response.data, "decisionSpeed", "decisionSpeed")
          );
        }
      });
    }, []);

    const [startupRiskOptions, setStartupRiskOptions] = useState<Option<string>[]>([]);
    useEffect(() => {
      getAllStartupRiskPreference().then((response) => {
        if (response?.data) {
          setStartupRiskOptions(
            mapToOptions(response.data, "startupRiskPreference", "startupRiskPreference")
          );
        }
      });
    }, []);

    const toEnumValue = (value: string): string => {
      return value
        .trim()
        .toUpperCase()
        .replace(/\s+/g, "_");
    };

    const getCompensationRange = (
      values: string[]
    ): { min: number | null; max: number | null } => {
      const compensationMap: Record<string, { min: number | null; max: number | null }> = {
        "₹10L - ₹15L": { min: 10, max: 15 },
        "₹15L - ₹20L": { min: 15, max: 20 },
        "₹20L - ₹30L": { min: 20, max: 30 },
        "₹30L - ₹40L": { min: 30, max: 40 },
        "₹40L - ₹60L": { min: 40, max: 60 },
        "₹60L - ₹80L": { min: 60, max: 80 },
        "₹80L - ₹1Cr": { min: 80, max: 100 },
        "₹1Cr+": { min: 100, max: null },
      };

      let min: number | null = null;
      let max: number | null = null;

      values.forEach((value) => {
        const range = compensationMap[value];
        if (!range) return;

        if (min === null || (range.min !== null && range.min < min)) {
          min = range.min;
        }

        if (range.max === null) {
          max = null;
        } else if (max !== null) {
          max = Math.max(max, range.max);
        } else {
          max = range.max;
        }
      });

      return { min, max };
    };

    const getExperienceRange = (
      values: string[]
    ): { min: number | null; max: number | null } => {
      const experienceMap: Record<string, { min: number; max: number | null }> = {
        "10-12 Yrs": { min: 10, max: 12 },
        "12-15 Yrs": { min: 12, max: 15 },
        "15-20 Yrs": { min: 15, max: 20 },
        "20+ Yrs": { min: 20, max: null },
      };

      let min: number | null = null;
      let max: number | null = null;

      values.forEach((value) => {
        const range = experienceMap[value];
        if (!range) return;

        if (min === null || range.min < min) {
          min = range.min;
        }

        if (range.max === null) {
          max = null;
        } else if (max !== null) {
          max = Math.max(max, range.max);
        } else {
          max = range.max;
        }
      });

      return { min, max };
    };

    const getNoticePeriodDays = (value: string): number => {
      const noticePeriodMap: Record<string, number> = {
        "Immediate": 0,
        "15 Days": 15,
        "30 Days": 30,
        "60 Days": 60,
        "90 Days": 90,
      };
      return noticePeriodMap[value] ?? 0;
    };

    const getTeamSizeRange = (
      value: string
    ): { min: number | null; max: number | null } => {
      const teamSizeMap: Record<string, { min: number | null; max: number | null }> = {
        "Direct: 1-10": { min: 1, max: 10 },
        "Org: 10-50": { min: 10, max: 50 },
        "Org: 50-150": { min: 50, max: 150 },
        "Org: 150+ Nodes": { min: 150, max: null },
      };
      return teamSizeMap[value] || { min: null, max: null };
    };

    const getLeadershipExperienceRange = (
      values: string[]
    ): { min: number | null; max: number | null } => {
      const map: Record<string, { min: number; max: number | null }> = {
        "0-3 Yrs": { min: 0, max: 3 },
        "3-5 Yrs": { min: 3, max: 5 },
        "5-10 Yrs": { min: 5, max: 10 },
        "10+ Yrs": { min: 10, max: null },
      };
      let min: number | null = null;
      let max: number | null = null;
      values.forEach((value) => {
        const range = map[value];
        if (!range) return;
        if (min === null || range.min < min) min = range.min;
        if (range.max === null) {
          max = null;
        } else if (max !== null) {
          max = Math.max(max, range.max);
        } else {
          max = range.max;
        }
      });
      return { min, max };
    };

    const getBudgetManagedRange = (
      values: string[]
    ): { min: number | null; max: number | null } => {
      const map: Record<string, { min: number; max: number | null }> = {
        "< ₹10L": { min: 0, max: 10 },
        "₹10L - ₹50L": { min: 10, max: 50 },
        "₹50L - ₹1Cr": { min: 50, max: 100 },
        "₹1Cr - ₹5Cr": { min: 100, max: 500 },
        "₹5Cr+": { min: 500, max: null },
      };
      let min: number | null = null;
      let max: number | null = null;
      values.forEach((value) => {
        const range = map[value];
        if (!range) return;
        if (min === null || range.min < min) min = range.min;
        if (range.max === null) {
          max = null;
        } else if (max !== null) {
          max = Math.max(max, range.max);
        } else {
          max = range.max;
        }
      });
      return { min, max };
    };

    const getRevenueScopeRange = (
      values: string[]
    ): { min: number | null; max: number | null } => {
      const map: Record<string, { min: number; max: number | null }> = {
        "< ₹1Cr": { min: 0, max: 1 },
        "₹1Cr - ₹10Cr": { min: 1, max: 10 },
        "₹10Cr - ₹50Cr": { min: 10, max: 50 },
        "₹50Cr - ₹100Cr": { min: 50, max: 100 },
        "₹100Cr+": { min: 100, max: null },
      };
      let min: number | null = null;
      let max: number | null = null;
      values.forEach((value) => {
        const range = map[value];
        if (!range) return;
        if (min === null || range.min < min) min = range.min;
        if (range.max === null) {
          max = null;
        } else if (max !== null) {
          max = Math.max(max, range.max);
        } else {
          max = range.max;
        }
      });
      return { min, max };
    };

    const getTravelWillingnessRange = (
      values: string[]
    ): { min: number | null; max: number | null } => {
      const map: Record<string, { min: number; max: number | null }> = {
        "0%": { min: 0, max: 0 },
        "Up to 25%": { min: 0, max: 25 },
        "Up to 50%": { min: 0, max: 50 },
        "Up to 75%": { min: 0, max: 75 },
        "100%": { min: 75, max: 100 },
      };
      let min: number | null = null;
      let max: number | null = null;
      values.forEach((value) => {
        const range = map[value];
        if (!range) return;
        if (min === null || range.min < min) min = range.min;
        if (range.max === null) {
          max = null;
        } else if (max !== null) {
          max = Math.max(max, range.max);
        } else {
          max = range.max;
        }
      });
      return { min, max };
    };

    const buildSearchPayload = (filters: FilterState) => {
      const payload: Record<string, any> = {};
      if (filters.searchQuery.trim()) payload.searchQuery = filters.searchQuery;
      if (filters.currentRole.length > 0) payload.currentRole = filters.currentRole;
      if (filters.targetRole.length > 0) payload.targetRole = filters.targetRole;
      if (filters.currentCompany.length > 0) payload.currentCompany = filters.currentCompany;
      if (filters.location.length > 0) payload.city = filters.location;
      if (filters.coreExpertise.length > 0) payload.coreExpertise = filters.coreExpertise;
      if (filters.domainExpertise.length > 0) payload.domainExpertise = filters.domainExpertise;
      if (filters.leadershipLevel.length > 0) payload.roleLevel = filters.leadershipLevel;
      if (filters.hiringAuthority.length > 0) payload.hiringAuthority = filters.hiringAuthority;
      if (filters.architectureExperience.length > 0) payload.architectureExperience = filters.architectureExperience;
      if (filters.productScale.length > 0) payload.productScale = filters.productScale;
      if (filters.workFlexibility.length > 0) payload.workFlexibility = filters.workFlexibility;
      if (filters.technologyStack.length > 0) payload.technologyStack = filters.technologyStack;
      if (filters.ownerShipLevel.length > 0) payload.ownerShipLevel = filters.ownerShipLevel;

      if (filters.availabilityStatus.length > 0) {
        payload.availabilityStatus = toEnumValue(filters.availabilityStatus[0]);
      }

      if (filters.expectedCompensation.length > 0) {
        payload.expectedCompensation = getCompensationRange(filters.expectedCompensation);
      }

      if (filters.totalExperience.length > 0) {
        payload.totalYearsOfExperience = getExperienceRange(filters.totalExperience);
      }

      if (filters.noticePeriod.length > 0) {
        payload.noticePeriodDays = getNoticePeriodDays(filters.noticePeriod[0]);
      }

      if (filters.teamSizeLed.length > 0) {
        payload.teamSizeLed = getTeamSizeRange(filters.teamSizeLed[0]);
      }

      if (filters.workStyle.length > 0) payload.workStyle = filters.workStyle;
      if (filters.careerIntent.length > 0) payload.careerIntent = filters.careerIntent;
      if (filters.decisionSpeed.length > 0) payload.decisionSpeed = filters.decisionSpeed;
      if (filters.interviewReadiness.length > 0) payload.interviewReadiness = filters.interviewReadiness;
      if (filters.startupRiskPreference.length > 0) payload.startupRiskPreference = filters.startupRiskPreference;

      if (filters.businessImpactType.length > 0) payload.businessImpactType = filters.businessImpactType;
      if (filters.businessImpactDescription.length > 0) payload.businessImpactDescription = filters.businessImpactDescription;
      if (filters.complexProblemSolved.length > 0) payload.complexProblemSolved = filters.complexProblemSolved;
      if (filters.topAchievements.length > 0) payload.topAchievements = filters.topAchievements;
      if (filters.reputationSignals.length > 0) payload.reputationSignals = filters.reputationSignals;
      if (filters.notableCompanies.length > 0) payload.notableCompanies = filters.notableCompanies;

      if (filters.leadershipScore > 0) payload.leadershipScore = filters.leadershipScore;
      if (filters.architectureScore > 0) payload.architectureScore = filters.architectureScore;
      if (filters.businessImpactScore > 0) payload.businessImpactScore = filters.businessImpactScore;
      if (filters.overallScore > 0) payload.overallScore = filters.overallScore;

      if (filters.profileVerified.length > 0) payload.profileVerified = filters.profileVerified.includes("Yes");
      if (filters.linkedinVerified.length > 0) payload.linkedinVerified = filters.linkedinVerified.includes("Yes");
      if (filters.workEmailVerified.length > 0) payload.workEmailVerified = filters.workEmailVerified.includes("Yes");
      if (filters.phoneVerified.length > 0) payload.phoneVerified = filters.phoneVerified.includes("Yes");

      // Leadership / Executive
      if (filters.yearsInLeadership.length > 0) payload.yearsInLeadership = getLeadershipExperienceRange(filters.yearsInLeadership);
      if (filters.reportsToLevel.length > 0) payload.reportsToLevel = filters.reportsToLevel;
      if (filters.highestEducation.length > 0) payload.highestEducation = filters.highestEducation;
      if (filters.functionScope.length > 0) payload.functionScope = filters.functionScope;
      if (filters.geographicScope.length > 0) payload.geographicScope = filters.geographicScope;
      if (filters.companyStageExperience.length > 0) payload.companyStageExperience = filters.companyStageExperience;
      if (filters.transformationExperience.length > 0) payload.transformationExperience = filters.transformationExperience;
      if (filters.executiveEducation.length > 0) payload.executiveEducation = filters.executiveEducation;
      if (filters.languages.length > 0) payload.languages = filters.languages;
      if (filters.budgetManagedLakhs.length > 0) payload.budgetManagedLakhs = getBudgetManagedRange(filters.budgetManagedLakhs);
      if (filters.revenueScopeCrores.length > 0) payload.revenueScopeCrores = getRevenueScopeRange(filters.revenueScopeCrores);
      if (filters.travelWillingnessPercent.length > 0) payload.travelWillingnessPercent = getTravelWillingnessRange(filters.travelWillingnessPercent);
      if (filters.boardOrInvestorExposure.length > 0) payload.boardOrInvestorExposure = filters.boardOrInvestorExposure.includes("Yes");

      return { filters: payload };
    };

    const [allFilters, setAllFilters] = useState<SearchFilterResponse[]>([]);
    const [allowedFilters, setAllowedFilters] = useState<SearchFilterResponse[]>([]);
    const [lockedFilters, setLockedFilters] = useState<Set<string>>(new Set());

    const isLocked = (fieldName: string) => lockedFilters.has(fieldName);

    useEffect(() => {
      const loadFilterConfiguration = async () => {
        try {
          const [allResponse, allowedResponse] = await Promise.all([
            getAllSearchFilters(),
            getAllSearchFiltersBySubscriptionPlan(storage.getUserId() ?? ""),
          ]);
          const all = allResponse.data || [];
          const allowed = allowedResponse.data || [];
          setAllFilters(all);
          setAllowedFilters(allowed);
          const allowedIds = new Set(allowed.map((filter) => filter.id));
          const locked = new Set(
            all.filter((filter) => !allowedIds.has(filter.id)).map((filter) => filter.fieldName)
          );
          setLockedFilters(locked);
        } catch (error) {
          console.error("Failed to load filters", error);
        }
      };
      if (storage.getUserId() ?? "") {
        loadFilterConfiguration();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storage.getUserId() ?? ""]);

    const buildSaveFilterPayload = (filterName: string): SaveFilterRequest => {
      const cleanedFilters = Object.entries(filters).reduce(
        (acc, [key, value]) => {
          if (Array.isArray(value) && value.length > 0) {
            acc[key] = value;
          }
          return acc;
        },
        {} as Record<string, any>
      );

      return {
        name: filterName,
        filterJson: JSON.stringify(cleanedFilters),
        isDefault: false,
        isAlertEnabled: false,
        alertFrequency: "DAILY",
      };
    };

    const [alert, setAlert] = useState<AlertProps | null>(null);
    const [selectedFilter, setSelectedFilter] = useState<SavedFilterResponse | null>(null);

    const handleSaveFilter = async (mode: "create" | "update") => {
      if (mode === "update" && !selectedFilter) {
        setAlert({
          variant: "error",
          title: "No Filter Selected",
          message: "Please select a saved filter first.",
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
        return;
      }

      let filterName = "";

      if (mode === "create") {
        filterName = window.prompt("Enter filter name", "My Saved Search") || "";
        if (!filterName) return;
      } else {
        filterName = selectedFilter?.name || "";
      }

      const payload = buildSaveFilterPayload(filterName);

      try {
        let response;

        if (mode === "create") {
          response = await saveFilter(payload);
        } else {
          response = await updateFilter(payload, selectedFilter!.id);
        }

        if (!response.success) {
          throw new Error(response.message);
        }

        setAlert({
          variant: "success",
          title: "Success!",
          message:
            mode === "create"
              ? "Filter created successfully."
              : "Filter updated successfully.",
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to save filter.";

        setAlert({
          variant: "error",
          title: mode === "create" ? "Create Filter Failed" : "Update Filter Failed",
          message: errorMessage,
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });

        console.error("Save Filter Error:", error);
      }
    };

    const activeChipCount = Object.entries(filters).reduce((count, [key, value]) => {
      if (key === "searchQuery") {
        return count + (filters.searchQuery.trim() ? 1 : 0);
      }
      if (Array.isArray(value)) {
        return count + (value as string[]).length;
      }
      return count + ((value as number) > 0 ? 1 : 0);
    }, 0);

    const renderActiveChips = () => {
      const chips: React.ReactNode[] = [];

      if (filters.searchQuery.trim()) {
        chips.push(
          <span
            key="searchQuery"
            className="inline-flex items-center gap-1.5 bg-slate-900 text-cyan-300 font-semibold border border-cyan-500/30 px-2.5 py-1 rounded-lg"
          >
            <Search className="w-3 h-3" />
            "{filters.searchQuery}"
            <button
              onClick={() => setFilters((prev) => ({ ...prev, searchQuery: "" }))}
              className="p-0.5 rounded-md hover:bg-slate-700 text-slate-500 hover:text-slate-200 transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        );
      }

      (Object.keys(filters) as (keyof FilterState)[]).forEach((key) => {
        const value = filters[key];

        if (key === "searchQuery") return;

        if (Array.isArray(value)) {
          (value as string[]).forEach((v) => {
            chips.push(
              <span
                key={`${key}:${v}`}
                className="inline-flex items-center gap-1.5 bg-slate-900 text-slate-200 font-semibold border border-slate-700 px-2.5 py-1 rounded-lg"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                {v}
                <button
                  onClick={() => handleCheckboxChange(key, v)}
                  className="p-0.5 rounded-md hover:bg-slate-700 text-slate-500 hover:text-slate-200 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          });
        } else if (value > 0) {
          chips.push(
            <span
              key={`${key}:min`}
              className="inline-flex items-center gap-1.5 bg-slate-900 text-purple-300 font-semibold border border-purple-500/30 px-2.5 py-1 rounded-lg"
            >
              <Award className="w-3 h-3" />
              {SCORE_LABELS[key] || key} ≥ {value}%
              <button
                onClick={() => handleSliderChange(key, 0)}
                className="p-0.5 rounded-md hover:bg-slate-700 text-slate-500 hover:text-slate-200 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          );
        }
      });

      return chips;
    };

    return (
      <div className="w-full max-w-7xl mx-auto space-y-5 text-slate-100">
        <div className="fixed top-5 right-5 z-[60] w-full max-w-md pointer-events-none">
          {alert && (
            <Alert
              variant={alert.variant}
              title={alert.title}
              message={alert.message}
              onClose={() => setAlert(null)}
            />
          )}
        </div>

        {/* ================= HEADER ================= */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-800/60 backdrop-blur-xl p-5 md:p-6 rounded-2xl border border-slate-700/50 shadow-2xl">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/10 shrink-0">
              <Zap className="w-5 h-5 text-slate-950 fill-current" />
            </div>
            <div>
              <h1 className="text-lg md:text-xl font-black text-white tracking-tight">
                Candidate Intelligence Engine
              </h1>
              <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-cyan-400 mt-0.5">
                Executive Talent Search Console
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="text-left sm:text-right flex-1 sm:flex-none">
              <p className="text-[10px] uppercase font-bold tracking-wider text-slate-400">
                Index Population
              </p>
              <p className="text-sm font-bold text-slate-100">
                {pagination.totalElements.toLocaleString()} candidates
              </p>
            </div>
            <span
              className={`text-xs font-bold tracking-wide px-4 py-2 rounded-xl border transition-all duration-300 whitespace-nowrap ${
                loading
                  ? "bg-amber-500/10 text-amber-400 border-amber-500/30 animate-pulse"
                  : "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
              }`}
            >
              {loading ? (
                <span className="inline-flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Searching...
                </span>
              ) : (
                "● Pipeline Live"
              )}
            </span>
          </div>
        </div>

        {/* ================= SEARCH BAR ================= */}
        <div className="bg-slate-800 rounded-2xl shadow-xl border border-slate-700/60 p-2.5 flex flex-col md:flex-row md:items-center gap-2.5">
          <div className="relative flex-1 flex items-center">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-slate-400" />
            </div>
            <input
              type="text"
              className="w-full bg-transparent pl-12 pr-10 py-4 text-white font-medium placeholder-slate-500 focus:outline-none text-sm"
              placeholder="Search by skills, roles, or keywords (e.g., Kubernetes, ex-FAANG, Principal)..."
              value={filters.searchQuery}
              onChange={(e) => setFilters((prev) => ({ ...prev, searchQuery: e.target.value }))}
            />
            {filters.searchQuery && (
              <button
                onClick={() => setFilters((prev) => ({ ...prev, searchQuery: "" }))}
                className="absolute inset-y-0 right-14 flex items-center text-slate-500 hover:text-slate-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="hidden lg:flex items-center gap-2 border-l border-slate-700 pl-4 pr-2 text-xs">
            <span className="text-[10px] font-bold uppercase text-slate-500">Hot:</span>
            {["Kafka Cluster", "Principal", "SaaS Scale"].map((tag) => (
              <button
                key={tag}
                onClick={() => setFilters((prev) => ({ ...prev, searchQuery: tag }))}
                className="bg-slate-700/50 text-slate-300 hover:text-white border border-slate-600/50 px-2.5 py-1 rounded-lg transition-all"
              >
                {tag}
              </button>
            ))}
          </div>

          <button
            onClick={executeSearch}
            className="inline-flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-400 active:scale-95 text-slate-950 text-xs font-black px-6 py-4 rounded-xl transition-all shadow-lg shadow-cyan-500/10 uppercase tracking-wider"
          >
            <Zap className="w-4 h-4 fill-current" />
            Search
          </button>
        </div>

        {/* ================= FILTER TOOLBAR ================= */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 bg-slate-800/80 rounded-2xl border border-slate-700/40 p-4 shadow-lg">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <SlidersHorizontal className="w-4 h-4 text-cyan-400 shrink-0" />
            <p className="text-xs font-bold uppercase tracking-wider text-slate-300">
              Active Filters
            </p>
            {activeChipCount > 0 && (
              <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-md bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                {activeChipCount}
              </span>
            )}
            {selectedFilter && (
              <span className="hidden md:inline-flex items-center gap-1 text-[10px] font-mono font-bold text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-md truncate max-w-[180px]">
                <Bookmark className="w-3 h-3 shrink-0" />
                {selectedFilter.name}
              </span>
            )}
          </div>

          <div className="flex items-center flex-wrap gap-2">
            <button
              onClick={() => handleSaveFilter("create")}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-400 hover:text-blue-300 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 px-3 py-2 rounded-xl transition-all"
            >
              <Bookmark className="w-3.5 h-3.5" />
              New Filter
            </button>
            <button
              onClick={() => handleSaveFilter("update")}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-400 hover:text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 px-3 py-2 rounded-xl transition-all"
            >
              <Save className="w-3.5 h-3.5" />
              Update
            </button>
            <button
              onClick={clearAllFilters}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-rose-400 hover:text-rose-300 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 px-3 py-2 rounded-xl transition-all"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Purge
            </button>
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="inline-flex items-center gap-1.5 text-xs font-bold bg-slate-700 hover:bg-slate-600 text-white px-3 py-2 rounded-xl border border-slate-600 transition-all"
            >
              {showAdvanced ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {showAdvanced ? "Hide Advanced" : "Advanced Filters"}
            </button>
          </div>
        </div>

        {/* ================= ACTIVE FILTER CHIPS ================= */}
        {activeChipCount > 0 && (
          <div className="flex flex-wrap gap-2 items-center bg-slate-800 p-4 rounded-2xl border border-slate-700/60 shadow-inner">
            {renderActiveChips()}
            <button
              onClick={clearAllFilters}
              className="text-xs font-bold text-rose-400 hover:text-rose-300 ml-auto bg-rose-500/10 border border-rose-500/20 px-3 py-1.5 rounded-lg uppercase tracking-wider transition-colors"
            >
              Clear All
            </button>
          </div>
        )}

        {/* ================= CORE FILTERS GRID ================= */}
        <div className="bg-slate-800/80 rounded-2xl p-5 border border-slate-700/40 shadow-lg">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-700/50">
            <SlidersHorizontal className="w-4 h-4 text-cyan-400" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-300">
              Core Directory Indexes
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            <FilterDropdown
              label="Current Role"
              options={currentRoleOptions.map((opt) => opt.value)}
              selected={filters.currentRole}
              onChange={(val) => handleCheckboxChange("currentRole", val)}
              locked={isLocked("currentRole")}
            />
            <FilterDropdown
              label="Targeted Role"
              options={roleOptions.map((opt) => opt.value)}
              selected={filters.targetRole}
              onChange={(val) => handleCheckboxChange("targetRole", val)}
              locked={isLocked("targetRole")}
            />
            <FilterDropdown
              label="Role Level"
              options={roleLevel.map((opt) => opt.value)}
              selected={filters.leadershipLevel}
              onChange={(val) => handleCheckboxChange("leadershipLevel", val)}
              locked={isLocked("leadershipLevel")}
            />
            <FilterDropdown
              label="Domain Expertise"
              options={domain.map((opt) => opt.value)}
              selected={filters.domainExpertise}
              onChange={(val) => handleCheckboxChange("domainExpertise", val)}
              locked={isLocked("domainExpertise")}
            />
            <FilterDropdown
              label="Experience"
              options={["10-12 Yrs", "12-15 Yrs", "15-20 Yrs", "20+ Yrs"]}
              selected={filters.totalExperience}
              onChange={(val) => handleCheckboxChange("totalExperience", val)}
              locked={isLocked("totalExperience")}
            />
            <FilterDropdown
              label="Location"
              options={city.map((opt) => opt.value)}
              selected={filters.location}
              onChange={(val) => handleCheckboxChange("location", val)}
              locked={isLocked("location")}
            />
          </div>
        </div>

        {/* ================= DECISION SIGNALS & WORK PREFERENCES ================= */}
        <div className="bg-slate-800/80 rounded-2xl p-5 border border-slate-700/40 shadow-lg">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-700/50">
            <Activity className="w-4 h-4 text-purple-400" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-300">
              Decision Signals & Work Preferences
            </h2>
            <span className="text-[10px] font-mono font-bold text-slate-500 ml-auto hidden sm:inline">
              Speed, readiness & risk appetite
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            <FilterDropdown
              label="Work Style"
              options={workStyleOptions.map((opt) => opt.value)}
              selected={filters.workStyle}
              onChange={(val) => handleCheckboxChange("workStyle", val)}
              locked={isLocked("workStyle")}
            />
            <FilterDropdown
              label="Decision Speed"
              options={decisionSpeedOptions.map((opt) => opt.value)}
              selected={filters.decisionSpeed}
              onChange={(val) => handleCheckboxChange("decisionSpeed", val)}
              locked={isLocked("decisionSpeed")}
            />
            <FilterDropdown
              label="Interview Readiness"
              options={INTERVIEW_READINESS_OPTIONS}
              selected={filters.interviewReadiness}
              onChange={(val) => handleCheckboxChange("interviewReadiness", val)}
              locked={isLocked("interviewReadiness")}
            />
            <FilterDropdown
              label="Startup Risk Appetite"
              options={startupRiskOptions.map((opt) => opt.value)}
              selected={filters.startupRiskPreference}
              onChange={(val) => handleCheckboxChange("startupRiskPreference", val)}
              locked={isLocked("startupRiskPreference")}
            />
            <FilterDropdown
              label="Career Intent"
              options={CAREER_INTENT_OPTIONS}
              selected={filters.careerIntent}
              onChange={(val) => handleCheckboxChange("careerIntent", val)}
              locked={isLocked("careerIntent")}
            />
          </div>
        </div>

        {/* ================= ADVANCED FILTERS ================= */}
        {showAdvanced && (
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
            {/* LEFT COLUMN */}
            <div className="xl:col-span-4 space-y-5">
              {/* Analytical Scoring sliders */}
              <div className="p-5 bg-gradient-to-b from-purple-900/20 to-slate-800/80 rounded-2xl border border-purple-500/20 shadow-xl space-y-5">
                <div className="flex items-center gap-2 border-b border-purple-500/20 pb-3">
                  <Award className="w-4 h-4 text-purple-400" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-purple-300">
                    Target Score Vectors
                  </h3>
                </div>

                {[
                  { label: "Overall Score", field: "overallScore", color: "accent-purple-500" },
                  { label: "Architecture Score", field: "architectureScore", color: "accent-cyan-500" },
                  { label: "Leadership Score", field: "leadershipScore", color: "accent-amber-500" },
                  { label: "Business Impact Score", field: "businessImpactScore", color: "accent-emerald-500" },
                ].map((slider) => {
                  const locked = isLocked(slider.field);
                  return (
                    <div
                      key={slider.field}
                      className={`space-y-1 bg-slate-900/50 p-3 rounded-xl border border-slate-700/40 ${
                        locked ? "opacity-60" : ""
                      }`}
                    >
                      <div className="flex justify-between text-xs font-bold">
                        <div className="flex items-center gap-1">
                          <span className="text-slate-300">{slider.label}</span>
                          {locked && <Lock className="w-3 h-3 text-slate-500" />}
                        </div>
                        <span className="text-purple-400 font-mono text-sm">
                          {filters[slider.field as keyof FilterState] as number}+
                        </span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        disabled={locked}
                        className={`w-full ${slider.color} bg-slate-700 h-1 rounded-lg ${
                          locked ? "cursor-not-allowed" : "cursor-pointer"
                        }`}
                        value={filters[slider.field as keyof FilterState] as number}
                        onChange={(e) =>
                          handleSliderChange(
                            slider.field as keyof FilterState,
                            parseInt(e.target.value)
                          )
                        }
                      />
                    </div>
                  );
                })}
              </div>

              {/* Financial Requirements */}
              <div className="p-5 bg-slate-800/80 rounded-2xl border border-slate-700/50 shadow-md space-y-4">
                <div className="flex items-center gap-2 border-b border-slate-700 pb-2">
                  <Activity className="w-4 h-4 text-emerald-400" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-300">
                    Financial Requirements
                  </h3>
                </div>
                <FilterSectionBlock
                  title="Compensation"
                  options={["₹10L - ₹15L", "₹15L - ₹20L", "₹20L - ₹30L", "₹30L - ₹40L", "₹40L - ₹60L", "₹60L - ₹80L", "₹80L - ₹1Cr", "₹1Cr+"]}
                  selected={filters.expectedCompensation}
                  onSelect={(val) => handleCheckboxChange("expectedCompensation", val)}
                  accentColor="emerald"
                  locked={isLocked("expectedCompensation")}
                />
                <FilterSectionBlock
                  title="Notice Period"
                  options={["Immediate", "15 Days", "30 Days", "60 Days", "90 Days"]}
                  selected={filters.noticePeriod}
                  onSelect={(val) => handleCheckboxChange("noticePeriod", val)}
                  accentColor="amber"
                  locked={isLocked("noticePeriod")}
                />
              </div>

              {/* Identity Trust Parameters */}
              <div className="p-5 bg-gradient-to-b from-cyan-950/20 to-slate-800/80 rounded-2xl border border-cyan-500/20 shadow-md space-y-3">
                <div className="flex items-center gap-2 border-b border-cyan-500/20 pb-2">
                  <ShieldCheck className="w-4 h-4 text-cyan-400" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-cyan-300">
                    Identity Trust Parameters
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: "Profile Verified", field: "profileVerified" },
                    { label: "LinkedIn Verified", field: "linkedinVerified" },
                    { label: "Work Email Verified", field: "workEmailVerified" },
                    { label: "Phone Verified", field: "phoneVerified" },
                  ].map((item) => (
                    <button
                      key={item.field}
                      onClick={() => handleCheckboxChange(item.field as keyof FilterState, "Yes")}
                      className={`p-2.5 rounded-xl border text-left transition-all ${
                        (filters[item.field as keyof FilterState] as string[]).includes("Yes")
                          ? "bg-cyan-500/10 border-cyan-400/60 text-cyan-400 shadow-md"
                          : "bg-slate-900/40 border-slate-700/60 text-slate-400 hover:border-slate-600"
                      }`}
                    >
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                        Security Gate
                      </p>
                      <p className="text-xs font-black truncate mt-0.5">{item.label}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="xl:col-span-8">
              <div className="bg-slate-800/60 rounded-2xl p-6 border border-slate-700/50 shadow-xl grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* GROUP 1: Technology / Architecture */}
                <div className="space-y-4 bg-slate-900/30 p-4 rounded-xl border border-slate-700/40">
                  <div className="flex items-center gap-2 border-b border-slate-700 pb-2">
                    <Cpu className="w-4 h-4 text-sky-400" />
                    <h4 className="text-xs font-black uppercase text-slate-200 tracking-wider">
                      Architecture & Blueprint Tech
                    </h4>
                  </div>
                  <FilterDropdown
                    label="Technology Stack"
                    options={technologyStack.map((opt) => opt.value)}
                    selected={filters.technologyStack}
                    onChange={(val) => handleCheckboxChange("technologyStack", val)}
                    locked={isLocked("technologyStack")}
                  />
                  <FilterDropdown
                    label="Architecture Experience"
                    options={architectureExperience.map((opt) => opt.value)}
                    selected={filters.architectureExperience}
                    onChange={(val) => handleCheckboxChange("architectureExperience", val)}
                    locked={isLocked("architectureExperience")}
                  />
                  <FilterDropdown
                    label="Core Expertise Experience"
                    options={coreExpertise.map((opt) => opt.value)}
                    selected={filters.coreExpertise}
                    onChange={(val) => handleCheckboxChange("coreExpertise", val)}
                    locked={isLocked("coreExpertise")}
                  />
                  <FilterDropdown
                    label="Product Scale"
                    options={productScale.map((opt) => opt.value)}
                    selected={filters.productScale}
                    onChange={(val) => handleCheckboxChange("productScale", val)}
                    locked={isLocked("productScale")}
                  />
                </div>

                {/* GROUP 2: Leadership Matrix */}
                <div className="space-y-4 bg-slate-900/30 p-4 rounded-xl border border-slate-700/40">
                  <div className="flex items-center gap-2 border-b border-slate-700 pb-2">
                    <Users className="w-4 h-4 text-orange-400" />
                    <h4 className="text-xs font-black uppercase text-slate-200 tracking-wider">
                      Organizational Footprint
                    </h4>
                  </div>
                  <FilterDropdown
                    label="Ownership Level"
                    options={ownerShipLevel.map((opt) => opt.value)}
                    selected={filters.ownerShipLevel}
                    onChange={(val) => handleCheckboxChange("ownerShipLevel", val)}
                    locked={isLocked("ownerShipLevel")}
                  />
                  <FilterDropdown
                    label="Hiring Authority"
                    options={hiringAuthority.map((opt) => opt.value)}
                    selected={filters.hiringAuthority}
                    onChange={(val) => handleCheckboxChange("hiringAuthority", val)}
                    locked={isLocked("hiringAuthority")}
                  />
                  <FilterSectionBlock
                    title="Team Size Led"
                    options={["Direct: 1-10", "Org: 10-50", "Org: 50-150", "Org: 150+ Nodes"]}
                    selected={filters.teamSizeLed}
                    onSelect={(val) => handleCheckboxChange("teamSizeLed", val)}
                    accentColor="orange"
                    locked={isLocked("teamSizeLed")}
                  />
                </div>

                {/* GROUP 3: Deployment Logistics */}
                <div className="space-y-4 bg-slate-900/30 p-4 rounded-xl border border-slate-700/40">
                  <div className="flex items-center gap-2 border-b border-slate-700 pb-2">
                    <Compass className="w-4 h-4 text-yellow-400" />
                    <h4 className="text-xs font-black uppercase text-slate-200 tracking-wider">
                      Availability & Cultural Logistics
                    </h4>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    <FilterSectionBlock
                      title="Availability Status"
                      options={["Actively exploring", "Open to selective offers", "Not actively looking"]}
                      selected={filters.availabilityStatus}
                      onSelect={(val) => handleCheckboxChange("availabilityStatus", val)}
                      accentColor="yellow"
                      locked={isLocked("availabilityStatus")}
                    />
                    <FilterDropdown
                      label="Work/Flexibility Allocation"
                      options={workFlexibility.map((opt) => opt.value)}
                      selected={filters.workFlexibility}
                      onChange={(val) => handleCheckboxChange("workFlexibility", val)}
                      locked={isLocked("workFlexibility")}
                    />
                  </div>
                </div>

                {/* GROUP 4: Executive Leadership */}
                <div className="space-y-4 bg-slate-900/30 p-4 rounded-xl border border-slate-700/40">
                  <div className="flex items-center gap-2 border-b border-slate-700 pb-2">
                    <Landmark className="w-4 h-4 text-rose-400" />
                    <h4 className="text-xs font-black uppercase text-slate-200 tracking-wider">
                      Executive Leadership
                    </h4>
                  </div>
                  <FilterSectionBlock
                    title="Years in Leadership"
                    options={["0-3 Yrs", "3-5 Yrs", "5-10 Yrs", "10+ Yrs"]}
                    selected={filters.yearsInLeadership}
                    onSelect={(val) => handleCheckboxChange("yearsInLeadership", val)}
                    accentColor="rose"
                    locked={isLocked("yearsInLeadership")}
                  />
                  <FilterSectionBlock
                    title="Reports To"
                    options={["CEO", "CTO", "CPO", "COO", "VP", "Board", "Investors"]}
                    selected={filters.reportsToLevel}
                    onSelect={(val) => handleCheckboxChange("reportsToLevel", val)}
                    accentColor="rose"
                    locked={isLocked("reportsToLevel")}
                  />
                  <FilterSectionBlock
                    title="Budget Managed (Lakhs)"
                    options={["< ₹10L", "₹10L - ₹50L", "₹50L - ₹1Cr", "₹1Cr - ₹5Cr", "₹5Cr+"]}
                    selected={filters.budgetManagedLakhs}
                    onSelect={(val) => handleCheckboxChange("budgetManagedLakhs", val)}
                    accentColor="rose"
                    locked={isLocked("budgetManagedLakhs")}
                  />
                  <FilterSectionBlock
                    title="Revenue Scope (Crores)"
                    options={["< ₹1Cr", "₹1Cr - ₹10Cr", "₹10Cr - ₹50Cr", "₹50Cr - ₹100Cr", "₹100Cr+"]}
                    selected={filters.revenueScopeCrores}
                    onSelect={(val) => handleCheckboxChange("revenueScopeCrores", val)}
                    accentColor="rose"
                    locked={isLocked("revenueScopeCrores")}
                  />
                  <FilterSectionBlock
                    title="Travel Willingness"
                    options={["0%", "Up to 25%", "Up to 50%", "Up to 75%", "100%"]}
                    selected={filters.travelWillingnessPercent}
                    onSelect={(val) => handleCheckboxChange("travelWillingnessPercent", val)}
                    accentColor="rose"
                    locked={isLocked("travelWillingnessPercent")}
                  />
                  <FilterDropdown
                    label="Highest Education"
                    options={["B.Tech", "M.Tech", "MBA", "PhD", "Other"]}
                    selected={filters.highestEducation}
                    onChange={(val) => handleCheckboxChange("highestEducation", val)}
                    locked={isLocked("highestEducation")}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: "Board / Investor Exposure", field: "boardOrInvestorExposure" },
                    ].map((item) => (
                      <button
                        key={item.field}
                        onClick={() => handleCheckboxChange(item.field as keyof FilterState, "Yes")}
                        className={`p-2.5 rounded-xl border text-left transition-all ${
                          (filters[item.field as keyof FilterState] as string[]).includes("Yes")
                            ? "bg-rose-500/10 border-rose-400/60 text-rose-400 shadow-md"
                            : "bg-slate-900/40 border-slate-700/60 text-slate-400 hover:border-slate-600"
                        }`}
                      >
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                          Leadership Gate
                        </p>
                        <p className="text-xs font-black truncate mt-0.5">{item.label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* GROUP 5: Executive Scope Tags */}
                <div className="space-y-4 bg-slate-900/30 p-4 rounded-xl border border-slate-700/40 md:col-span-2">
                  <div className="flex items-center gap-2 border-b border-slate-700 pb-2">
                    <Users className="w-4 h-4 text-indigo-400" />
                    <h4 className="text-xs font-black uppercase text-slate-200 tracking-wider">
                      Executive Scope Tags
                    </h4>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <FilterDropdown
                      label="Function Scope"
                      options={["Engineering", "Product", "Data", "Security", "Operations", "Sales", "Marketing", "Finance"]}
                      selected={filters.functionScope}
                      onChange={(val) => handleCheckboxChange("functionScope", val)}
                      locked={isLocked("functionScope")}
                    />
                    <FilterDropdown
                      label="Geographic Scope"
                      options={["India", "APAC", "EMEA", "North America", "LATAM", "Global"]}
                      selected={filters.geographicScope}
                      onChange={(val) => handleCheckboxChange("geographicScope", val)}
                      locked={isLocked("geographicScope")}
                    />
                    <FilterDropdown
                      label="Company Stage Experience"
                      options={["Seed", "Series A", "Series B", "Growth", "Enterprise", "Startup"]}
                      selected={filters.companyStageExperience}
                      onChange={(val) => handleCheckboxChange("companyStageExperience", val)}
                      locked={isLocked("companyStageExperience")}
                    />
                    <FilterDropdown
                      label="Transformation Experience"
                      options={["M&A", "Turnaround", "Migration", "Scaling", "Cost Restructure", "IPO"]}
                      selected={filters.transformationExperience}
                      onChange={(val) => handleCheckboxChange("transformationExperience", val)}
                      locked={isLocked("transformationExperience")}
                    />
                    <FilterDropdown
                      label="Executive Education"
                      options={["MBA", "INSEAD", "ISB", "IIM", "MIT Sloan", "Wharton"]}
                      selected={filters.executiveEducation}
                      onChange={(val) => handleCheckboxChange("executiveEducation", val)}
                      locked={isLocked("executiveEducation")}
                    />
                    <FilterDropdown
                      label="Languages"
                      options={["English", "Hindi", "Tamil", "Telugu", "Bengali", "Marathi", "Spanish", "Mandarin"]}
                      selected={filters.languages}
                      onChange={(val) => handleCheckboxChange("languages", val)}
                      locked={isLocked("languages")}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ================= RESULTS ================= */}
        <div className="bg-slate-800 rounded-2xl p-4 md:p-6 border border-slate-700/60 shadow-2xl">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-300">
              Match Results
            </h2>
            {!loading && results.length > 0 && (
              <span className="text-xs font-mono font-semibold text-slate-400">
                {results.length.toLocaleString()} shown of{" "}
                <span className="text-cyan-400 font-bold">
                  {pagination.totalElements.toLocaleString()}
                </span>
              </span>
            )}
          </div>

          {loading && results.length === 0 ? (
            <div className="min-h-[260px] flex flex-col justify-center items-center space-y-4">
              <Loader2 className="w-10 h-10 text-cyan-400 animate-spin" />
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest animate-pulse">
                Compiling candidate match results...
              </p>
            </div>
          ) : results.length === 0 ? (
            <div className="min-h-[260px] flex flex-col justify-center items-center text-center p-6">
              <div className="w-12 h-12 rounded-2xl bg-slate-900 border border-slate-700 flex items-center justify-center mb-3">
                <Inbox className="w-5 h-5 text-slate-500" />
              </div>
              <p className="text-sm font-bold text-slate-300">No Matching Candidates</p>
              <p className="text-xs text-slate-500 mt-1 max-w-xs">
                Try lowering score thresholds or removing some filters to widen the pool.
              </p>
              <button
                onClick={clearAllFilters}
                className="mt-4 text-xs font-bold text-cyan-400 hover:text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 px-4 py-2 rounded-xl transition-all uppercase tracking-wider"
              >
                Reset All Filters
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {results.map((candidate) => (
                <CandidateCardForRecruiter key={candidate.id} candidate={candidate} />
              ))}
              <div className="pt-4 border-t border-slate-700/60">
                <LoadMoreButton
                  loading={loading}
                  hasNext={pagination.hasNext}
                  page={page}
                  size={pagination.size}
                  totalElements={pagination.totalElements}
                  currentCount={results.length}
                  setPage={setPage}
                  variant="dark"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default ExecutiveSearchPortalLight;

/* ================= MULTI-SELECT DROPDOWN ================= */
interface DropdownProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (value: string) => void;
  locked?: boolean;
}

function FilterDropdown({ label, options, selected, onChange, locked = false }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pos, setPos] = useState<{ top: number; left: number; width: number; up: boolean } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const closeDropdown = () => {
    setOpen(false);
    setSearchTerm("");
  };

  useEffect(() => {
    if (!open) return;
    const onViewportChange = (e: Event) => {
      const target = e.target as HTMLElement;
      if (panelRef.current && panelRef.current.contains(target)) return;
      closeDropdown();
    };
    window.addEventListener("scroll", onViewportChange, true);
    window.addEventListener("resize", onViewportChange);
    return () => {
      window.removeEventListener("scroll", onViewportChange, true);
      window.removeEventListener("resize", onViewportChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleDropdown = () => {
    if (locked) return;
    if (open) {
      closeDropdown();
      return;
    }
    const btn = buttonRef.current;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      const width = Math.min(Math.max(rect.width, 280), window.innerWidth - 16);
      const left = Math.min(Math.max(rect.left, 8), window.innerWidth - width - 8);
      const spaceBelow = window.innerHeight - rect.bottom;
      const up = spaceBelow < 280;
      setPos({ top: up ? rect.top : rect.bottom + 8, left, width, up });
    }
    setOpen(true);
  };

  const filteredOptions = options.filter((option) =>
    option?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="w-full text-left">
      <button
        ref={buttonRef}
        disabled={locked}
        onClick={toggleDropdown}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`w-full flex items-center justify-between text-xs font-bold px-3.5 py-3.5 rounded-xl border transition-all ${
          locked
            ? "bg-slate-900/50 border-slate-800 text-slate-600 cursor-not-allowed"
            : selected.length > 0
            ? "bg-cyan-500/10 border-cyan-500/50 text-cyan-400 shadow-md shadow-cyan-950/20"
            : "bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-600"
        }`}
      >
        <span className="truncate pr-2">
          {selected.length > 0 ? `${label} (${selected.length})` : label}
        </span>
        {locked ? (
          <Lock className="w-3 h-3 text-slate-600 flex-shrink-0" />
        ) : (
          <ChevronDown
            className={`w-3 h-3 text-slate-400 flex-shrink-0 transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        )}
      </button>

      {open &&
        pos &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[80]" onClick={closeDropdown} />
            <div
              ref={panelRef}
              role="listbox"
              className="z-[90] rounded-xl bg-slate-900 border border-slate-700 shadow-2xl p-2 animate-fadeIn overflow-hidden flex flex-col"
              style={{
                position: "fixed",
                left: pos.left,
                width: pos.width,
                top: pos.up ? undefined : pos.top,
                bottom: pos.up ? window.innerHeight - pos.top : undefined,
                maxHeight: pos.up ? pos.top - 8 : window.innerHeight - pos.top - 8,
              }}
            >
              <div className="px-2 py-1.5 border-b border-slate-800 mb-2 shrink-0">
                <input
                  autoFocus
                  type="text"
                  placeholder={`Search ${label}...`}
                  className="w-full bg-slate-800 text-xs text-white rounded-lg px-2.5 py-1.5 placeholder-slate-500 focus:outline-none border border-slate-700"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") closeDropdown();
                  }}
                />
              </div>
              <div className="flex-1 overflow-y-auto space-y-0.5 custom-scrollbar">
                {filteredOptions.length === 0 ? (
                  <p className="text-[11px] text-slate-500 p-2 text-center">No matches</p>
                ) : (
                  filteredOptions.map((option) => {
                    const isChecked = selected.includes(option);
                    return (
                      <button
                        key={option}
                        onClick={() => onChange(option)}
                        className={`w-full flex items-center gap-2 px-2.5 py-2 text-xs font-semibold rounded-lg text-left transition-all ${
                          isChecked
                            ? "bg-cyan-500/10 text-cyan-400 font-bold"
                            : "text-slate-400 hover:bg-slate-800/80 hover:text-white"
                        }`}
                      >
                        <div
                          className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 transition-all ${
                            isChecked ? "border-cyan-400 bg-cyan-400" : "border-slate-600 bg-slate-800"
                          }`}
                        >
                          {isChecked && <Check className="w-2.5 h-2.5 text-slate-950 stroke-[4]" />}
                        </div>
                        <span className="truncate">{option}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}

/* ================= PILL LIST SELECTION ================= */
interface BlockProps {
  title: string;
  options: string[];
  selected: string[];
  onSelect: (value: string) => void;
  accentColor: "indigo" | "violet" | "pink" | "amber" | "emerald" | "sky" | "orange" | "yellow" | "rose";
  locked?: boolean;
}

function FilterSectionBlock({ title, options, selected, onSelect, accentColor, locked = false }: BlockProps) {
  const colorMap = {
    indigo: { text: "text-indigo-400", bg: "bg-indigo-500/10", border: "border-indigo-500/40" },
    violet: { text: "text-violet-400", bg: "bg-violet-500/10", border: "border-violet-500/40" },
    pink: { text: "text-pink-400", bg: "bg-pink-500/10", border: "border-pink-500/40" },
    amber: { text: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/40" },
    emerald: { text: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/40" },
    sky: { text: "text-sky-400", bg: "bg-sky-500/10", border: "border-sky-500/40" },
    orange: { text: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/40" },
    yellow: { text: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/40" },
    rose: { text: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/40" },
  };

  const active = colorMap[accentColor];

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        <label className="text-[10px] font-black uppercase text-slate-500 tracking-wider block">
          {title}
        </label>
        {locked && <Lock className="w-3 h-3 text-slate-600" />}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => {
          const isSelected = selected.includes(option);
          return (
            <button
              key={option}
              type="button"
              disabled={locked}
              onClick={() => !locked && onSelect(option)}
              className={`text-[11px] font-bold px-2.5 py-1.5 rounded-lg border transition-all ${
                locked
                  ? "bg-slate-900/50 border-slate-800 text-slate-600 cursor-not-allowed opacity-60"
                  : isSelected
                  ? `${active.bg} ${active.border} ${active.text} shadow-sm`
                  : "bg-slate-900/40 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-200"
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}